import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "sonner";
import { fmtEur } from "@/lib/format";
import { Megaphone, Mail, MessageSquare, Send, Users, AlertTriangle, RefreshCw, Info } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STAGE_STYLE = {
  herinnering_1: "bg-sky-100 text-sky-700 border-sky-200",
  herinnering_2: "bg-amber-100 text-amber-700 border-amber-200",
  aanmaning: "bg-red-100 text-red-700 border-red-200",
  geen: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function BulkDunning() {
  const [data, setData] = useState({ groups: [], twilio_configured: false, templates: [] });
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("");
  const [channels, setChannels] = useState({ email: true, sms: false });
  const [subject, setSubject] = useState("Herinnering openstaand bedrag");
  const [body, setBody] = useState("Beste {naam},\n\nWij zien dat er nog een bedrag van {bedrag} openstaat. Wilt u dit zo spoedig mogelijk voldoen?\n\nMet vriendelijke groet,\nPraktisch-Debiteurenbeheer");
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/bulk-dunning/overview").then((r) => {
      setData(r.data);
      if (!activeStage && r.data.groups?.length) setActiveStage(r.data.groups[0].stage);
    }).catch(() => toast.error("Laden mislukt")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const group = useMemo(() => data.groups.find((g) => g.stage === activeStage), [data, activeStage]);

  useEffect(() => {
    // standaard alle debiteuren in de fase geselecteerd
    if (group) {
      const init = {};
      group.tenants.forEach((t) => { init[t.id] = true; });
      setSelected(init);
    }
  }, [activeStage]); // eslint-disable-line

  const selectedTenants = useMemo(() => (group?.tenants || []).filter((t) => selected[t.id]), [group, selected]);
  const allChecked = group && selectedTenants.length === group.tenants.length;

  const toggleAll = () => {
    if (!group) return;
    const next = {};
    if (!allChecked) group.tenants.forEach((t) => { next[t.id] = true; });
    setSelected(next);
  };

  const applyTemplate = (label) => {
    const tpl = (data.templates || []).find((t) => t.label === label);
    if (tpl) { setSubject(tpl.subject || ""); setBody(tpl.body || ""); }
  };

  const canSend = group && selectedTenants.length > 0 && (channels.email || channels.sms);

  const doSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const chans = [];
      if (channels.email) chans.push("email");
      if (channels.sms) chans.push("sms");
      const r = await api.post("/bulk-dunning/send", {
        stage: activeStage, channels: chans, subject, body,
        tenant_ids: selectedTenants.map((t) => t.id),
      });
      const s = r.data;
      const parts = [];
      if (s.email_sent) parts.push(`${s.email_sent} e-mail(s)`);
      if (s.sms_sent) parts.push(`${s.sms_sent} SMS`);
      if (s.sms_mocked) parts.push(`${s.sms_mocked} SMS (mock)`);
      toast.success(`Verstuurd naar ${s.total} debiteur(en): ${parts.join(", ") || "niets"}`);
      if (s.errors?.length) toast.warning(`${s.errors.length} waarschuwing(en): ${s.errors.slice(0, 3).join(" · ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="bulk-dunning-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white"><Megaphone className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-800">Bulk-aanmaningen</h1>
            <p className="text-sm text-slate-500">Verstuur in één keer een herinnering per e-mail en/of SMS naar alle debiteuren in een aanmaningsfase.</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="bulk-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Vernieuwen</Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Laden…</p>
      ) : data.groups.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="bulk-empty">Geen debiteuren met openstaande bedragen gevonden.</Card>
      ) : (
        <>
          {/* Fase-keuze */}
          <div className="flex flex-wrap gap-3" data-testid="bulk-stage-tabs">
            {data.groups.map((g) => (
              <button
                key={g.stage}
                onClick={() => setActiveStage(g.stage)}
                data-testid={`bulk-stage-${g.stage}`}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${activeStage === g.stage ? "ring-2 ring-emerald-500 border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <div className="flex items-center gap-2">
                  <Badge className={STAGE_STYLE[g.stage]}>{g.label}</Badge>
                  <span className="text-sm font-semibold text-slate-700">{g.count}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">Openstaand: {fmtEur(g.total_outstanding)}</div>
                <div className="mt-0.5 text-[11px] text-slate-400">{g.with_email} met e-mail · {g.with_mobile} met mobiel</div>
              </button>
            ))}
          </div>

          {group && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Debiteurenlijst */}
              <Card className="lg:col-span-3 p-5 space-y-3" data-testid="bulk-tenant-list">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Users className="h-4 w-4" /> Debiteuren in fase "{group.label}"</h2>
                  <button className="text-xs text-emerald-700 font-medium" onClick={toggleAll} data-testid="bulk-toggle-all">
                    {allChecked ? "Alles deselecteren" : "Alles selecteren"}
                  </button>
                </div>
                <div className="divide-y max-h-[460px] overflow-y-auto -mx-2">
                  {group.tenants.map((t) => (
                    <label key={t.id} className="flex items-center gap-3 px-2 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`bulk-tenant-row-${t.id}`}>
                      <Checkbox checked={!!selected[t.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [t.id]: !!v }))} data-testid={`bulk-tenant-check-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
                        <div className="text-xs text-slate-500">Openstaand {fmtEur(t.outstanding)}{t.overdue_amount ? ` · te laat ${fmtEur(t.overdue_amount)}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Mail className={`h-3.5 w-3.5 ${t.has_email ? "text-emerald-600" : "text-slate-300"}`} title={t.has_email ? "E-mail beschikbaar" : "Geen e-mail"} />
                        <MessageSquare className={`h-3.5 w-3.5 ${t.has_mobile ? "text-emerald-600" : "text-slate-300"}`} title={t.has_mobile ? "Mobiel beschikbaar" : "Geen mobiel"} />
                      </div>
                    </label>
                  ))}
                </div>
              </Card>

              {/* Bericht opstellen */}
              <Card className="lg:col-span-2 p-5 space-y-4" data-testid="bulk-compose">
                <h2 className="font-heading font-semibold text-slate-800">Bericht</h2>

                <div>
                  <Label className="text-xs">Kanalen</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={channels.email} onCheckedChange={(v) => setChannels((c) => ({ ...c, email: !!v }))} data-testid="bulk-channel-email" />
                      <Mail className="h-4 w-4 text-slate-500" /> E-mail
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={channels.sms} onCheckedChange={(v) => setChannels((c) => ({ ...c, sms: !!v }))} data-testid="bulk-channel-sms" />
                      <MessageSquare className="h-4 w-4 text-slate-500" /> SMS
                    </label>
                  </div>
                  {channels.sms && !data.twilio_configured && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="bulk-sms-mock-warning">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>SMS is nog niet gekoppeld — berichten worden als <strong>mock</strong> gelogd. Koppel Twilio in Instellingen → SMS om echt te versturen.</span>
                    </div>
                  )}
                </div>

                {data.templates?.length > 0 && (
                  <div>
                    <Label className="text-xs">Sjabloon</Label>
                    <Combobox testId="bulk-template-select" value="" onChange={applyTemplate} placeholder="Kies een sjabloon…" searchPlaceholder="Zoek sjabloon…" options={data.templates.map((t) => ({ value: t.label, label: t.label }))} />
                  </div>
                )}

                <div>
                  <Label className="text-xs">Onderwerp (e-mail)</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-subject" />
                </div>
                <div>
                  <Label className="text-xs">Bericht</Label>
                  <Textarea rows={7} value={body} onChange={(e) => setBody(e.target.value)} data-testid="bulk-body" />
                  <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><Info className="h-3 w-3" /> Gebruik <code>{"{naam}"}</code> en <code>{"{bedrag}"}</code> als placeholders.</p>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!canSend || sending} data-testid="bulk-send-btn">
                      <Send className="h-4 w-4 mr-2" /> {sending ? "Versturen…" : `Verstuur naar ${selectedTenants.length} debiteur(en)`}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Bulk-aanmaning versturen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        U staat op het punt om via {[channels.email && "e-mail", channels.sms && "SMS"].filter(Boolean).join(" en ")} een bericht te sturen naar <strong>{selectedTenants.length}</strong> debiteur(en) in fase "{group.label}". Dit wordt vastgelegd in de tijdslijn.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuleren</AlertDialogCancel>
                      <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" onClick={doSend} data-testid="bulk-send-confirm">Ja, versturen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
